import styled from 'styled-components';
import tenantTheme from '@theme';

/**
 * Sits under the fixed header (z-index 999) and the fixed rail (z-index 1002) so both keep their
 * own inner scrim instead, which is what lets the open trigger stay lit while its chrome dims.
 */
export const BodyScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 998;
  background: rgba(0, 0, 0, 0.45);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: opacity 0.18s ease;
`;

/** Covers the fixed rail, which sits above the body scrim in the stacking order. */
export const RailScrim = styled.div`
  position: fixed;
  z-index: 1003;
  background: rgba(0, 0, 0, 0.45);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: opacity 0.18s ease;
`;

/**
 * Fixed to the header's own rect rather than inset in its parent: antd's Col is positioned, so
 * an absolutely positioned scrim would cover only the column it is rendered in. It stays a child
 * of the header so the header's stacking context keeps it under the lit CTA.
 */
export const ChromeScrim = styled.div`
  position: fixed;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: opacity 0.18s ease;
`;

export const TriggerWrap = styled.div`
  position: relative;
  z-index: 1001;
  display: inline-flex;
`;

export const RailTriggerWrap = styled.span`
  display: inline-grid;
  place-content: center;
`;

export const MenuPositioner = styled.div`
  position: absolute;
  min-width: 318px;
  max-width: calc(100vw - 24px);
  z-index: 1100;
  /* The rail flyout is portalled to document.body, which carries neither font nor size of its
     own — both reach components through the antd theme token. Name them here so the panel matches
     the chrome around it wherever it is mounted; otherwise the portalled copy falls back to a
     system Naskh at the browser's default 16px while the header copy inherits the app's 14px.
     Read off the imported theme, not the styled-components one: the rail renders under a nested
     ThemeProvider holding tenantTheme.darkTheme, which carries no themeTokens. */
  font-family: ${tenantTheme?.themeTokens?.token?.fontFamily || 'inherit'};
  font-size: ${tenantTheme?.themeTokens?.token?.fontSize ? `${tenantTheme.themeTokens.token.fontSize}px` : 'inherit'};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;

  ${({ $placement, $open, $rtl }) =>
    $placement === 'left_nav'
      ? /* Fixed and portalled to the body: the rail's scroll container clips overflow on both
           axes, so an absolutely positioned flyout inside it could never escape the rail.
           Coordinates come from the menu item's rect; the offset centres the caret on it. */
        `
        position: fixed;
        padding-inline-start: 10px;
        transform: translate(${$open ? '0' : `${$rtl ? '4px' : '-4px'}`}, -27.5px);
      `
      : `
        top: 100%;
        inset-inline-end: 0;
        padding-top: 6px;
        transform: ${$open ? 'translateY(0)' : 'translateY(-4px)'};
      `}
`;

export const MenuPanel = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  box-shadow: rgba(65, 65, 65, 0.18) 0 6px 21px -6px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const MenuCaret = styled.span`
  position: absolute;
  top: 22px;
  inset-inline-start: 5px;
  width: 11px;
  height: 11px;
  background: #fff;
  border-inline-start: 1px solid #f0f0f0;
  ${({ $rtl }) =>
    $rtl
      ? `border-block-start: 1px solid #f0f0f0; border-start-start-radius: 2px;`
      : `border-block-end: 1px solid #f0f0f0; border-end-start-radius: 2px;`}
  transform: rotate(45deg);
  z-index: 1;
`;

export const MenuRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.88);
  /* Buttons do not inherit the page font, and the app has no global reset for it. Without this
     the rows fall back to the browser's default, which is obvious in Arabic. */
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  text-align: start;
  white-space: nowrap;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: #f2fafa;
    color: #28b16d;
  }
`;

export const RowLead = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const RowIcon = styled.span`
  display: grid;
  place-content: center;
  flex: 0 0 auto;
  width: 34px;
  aspect-ratio: 1 / 1;
`;

export const RowLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowTrailing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`;

/** Matches the pill the ad-license card already uses, so the two read as the same badge. */
export const NewChip = styled.span`
  display: inline-block;
  background: ${tenantTheme['danger-color']};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  border-radius: 16px;

  /* Neither script's ink lands in the middle of the line box on its own: Latin rides 0.5px high,
     and Arabic — drawn by Droid Arabic Kufi while Figtree sizes the box — sits 1.5px low. Both
     paddings still total 6px, so the pill stays 18px either way.
     Direction arrives as a prop rather than from the theme, because the rail renders under a
     nested ThemeProvider holding tenantTheme.darkTheme, which carries no rtl. */
  padding: ${({ $rtl }) => ($rtl ? '1.5px 6px 4.5px' : '3.5px 6px 2.5px')};
`;
